
import { Component, Vue, Watch } from 'vue-property-decorator';
import {
  EmployeeLeavesInterface,
  LeaveCalendarScope,
  LeaveRequestInterface,
  LeavesRepositoryInterface,
  LeaveTypeInterface,
} from '@/services/Calendar/leaves/interfaces';
import { UserRepoInterface } from '@/services/Users/Interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import { CreateLeaveRequestFrom } from '@/services/Calendar/leaves/forms';
import ShiftRepositoryInterface from '@/services/Calendar/Shifts/Interfaces';
import ShiftsComponent from '@/views/pages/calendar/leaves/components/ShiftsComponent.vue';
import EventsRepositoryInterface from '@/services/Calendar/Events/Interfaces';
import {
  BaseProfileDataInterface,
  ProfileRepositoryInterface,
} from '@/services/Users-profile/interfaces';

@Component({
  components: {
    ShiftsComponent,
  },

  inject: [
    'leavesRepository',
    'userRepository',
    'profileRepository',
    'shiftRepository',
    'eventsRepository',
  ],
})
export default class NewLeave extends Vue {
  private loading: boolean = true;
  private leavesRepository!: LeavesRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private shiftRepository!: ShiftRepositoryInterface;
  private eventsRepository!: EventsRepositoryInterface;
  private profileRepository: ProfileRepositoryInterface;
  private leaveTypes: LeaveTypeInterface[] = [];
  private users: BaseProfileDataInterface[] = [];
  private employeeLeaves!: EmployeeLeavesInterface;
  private form: WPXFormValue<CreateLeaveRequestFrom> = new WPXFormValue(CreateLeaveRequestFrom);
  private validDates: boolean = false;
  private userLeaves: LeaveRequestInterface[] = [];
  private userLeavesDatesStrings: string[] = [];
  private eventDays: string[] = [];
  private holidays: string[] = [];
  private duties: string[] = [];
  private currentMonth: string = new Date().toISOString()
    .substr(0, 7);
  private documentInput: boolean = false;
  private inputLeaveTypes: string[] =
    ['Sick leave', 'Leave for marriage', 'Leave for blood donation', 'Leave for funeral', 'Paternity leave'];
  private leaveDocument: File | undefined  = undefined;
  private ready = false;

  private async created(): Promise<void> {
    this.userLeaves = await this.leavesRepository.getUserApprovedLeaves(this.$store.getters.id);
    const leaveTypes = await this.leavesRepository.getLeaveTypes();
    this.leaveTypes = leaveTypes.filter((type) => type.id !== 0);

    const approvers = await this.userRepository.getUsersByScope(LeaveCalendarScope.LEAVE_REQUEST_REVIEW);
    const defaultApprovers = await this.leavesRepository.getApproversByDepartment(this.$store.getters.departmentId);
    if (defaultApprovers) {
      this.form.data.firstApprover = defaultApprovers.firstApprover;
      this.form.data.secondApprover = defaultApprovers.secondApprover;
    }

    this.users = await this.profileRepository
      .getBaseProfileDataByUsersIds(approvers.map((approver) => String(approver.id)));
    this.employeeLeaves = await this.leavesRepository.getEmployeeLeaves(this.$store.getters.id);

    if ((!this.employeeLeaves)) {
      this.$errorDialog.show('Missing Employee Leaves details, please check your profile');
      return;
    }

    await this.getUserShifts();
    await this.getEvents();

    this.userLeaves.forEach((leave) => {
      const raw = this.getDatesArray(leave.dateFrom, leave.dateTo);

      const normalized = raw.map((date) => new Date(date).toISOString().slice(0, 10));

      this.userLeavesDatesStrings.push(...normalized);
      this.eventDays.push(...normalized);
    });

    this.loading = false;
    this.ready = true;
  }

  private async getEvents(): Promise<void> {
    const [year, month] = this.currentMonth.split('-')
      .map((str) => Number(str));

    const events = await this.eventsRepository.getEventsForMonth(year, month);
    this.holidays = events.filter(holiday => holiday.type === 0).map((holiday) => (new Date(holiday.date)).toISOString()
      .substr(0, 10));

    this.eventDays.push(...this.holidays.filter((holiday) => !this.duties.includes(holiday)));
  }

  private async getUserShifts(): Promise<void> {
    const shifts = await this.shiftRepository.getUserShifts(this.$store.getters.id, new Date(this.currentMonth));
    this.duties = shifts.map((shift) => shift.date.substr(0, 10));
  }

  private async createRequest(): Promise<void> {
    const request = this.extractRequest();
    if (!this.validDaysCount(request)) {
      return;
    }

    try {
      const dateFrom = new Date(request[0]);
      const dateTo = new Date(request[request.length - 1]);


      const requestData = {
        dateFrom,
        dateTo,
        requestBy: this.$store.getters.id,
        firstApprover: this.form.data.firstApprover,
        secondApprover: this.form.data.secondApprover,
        leaveTypeId: this.form.data.type,
        selectedDays: JSON.stringify(request),
        notes: this.form.data.notes,
      };
      await this.leavesRepository.createRequest(requestData, this.leaveDocument);

      this.$successDialog.show('Leave Request Created Successfully');
     } catch (e) {
      this.$errorDialog.show(e.response.data.message);
    }
  }

  private validDaysCount(request: string[]): boolean {
    const leaveType = this.leaveTypes.find((type) => type.id === this.form.data.type);

    if (!leaveType) {
      this.$errorDialog.show('Invalid leave type');
      return false;
    }

    if (!request.length) {
      this.$errorDialog.show('Please select at least one day');
      return false;
    }

    const daysCount = request.length;

    if (leaveType.name === 'Paid leave' && daysCount > this.employeeLeaves.paidLeaves) {
      this.$errorDialog.show('Not Enough Paid Leaves!');
      return false;
    }

    if (leaveType.maxDays !== -1 && daysCount > leaveType.maxDays) {
      this.$errorDialog.show(
        `Invalid Days Count! ${leaveType.name} - ${leaveType.maxDays} consecutive days`,
      );
      return false;
    }

    return true;
  }

  private extractRequest(): string[] {
    // Getting all selected dates
    return this.form.data.dates.sort();
  }

  private getEventDates(calendarDate: string): string[] {
    const date = new Date(calendarDate);
    const colors = [];
    if (this.userLeavesDatesStrings.includes(calendarDate)) {
      colors.push('primary');
    }

    if (this.holidays.includes(calendarDate)) {
      colors.push('green');
    }

    if (this.duties.includes(calendarDate) && !this.userLeavesDatesStrings.includes(calendarDate)) {
      colors.push('red');
    }

    return colors;
  }

  @Watch('currentMonth')
  private async monthChanged(): Promise<void> {
    if (this.currentMonth.length < 7) {
      return;
    }

    await this.getUserShifts();
    await this.getEvents();
  }

  private getDatesArray(firstDate: string, lastDate: string): string[] {
    const arr: Date[] = [];
    for (const dt = new Date(firstDate); dt <= new Date(lastDate); dt.setDate(dt.getDate() + 1)) {
      arr.push(new Date(dt));
    }

    return arr.map((date) => date.toISOString()
      .replace(',', '')
      .substr(0, 10));
  }

  private allowedDates(val: string): boolean {
    const currentDate = new Date(val);
    const leaveType = this.leaveTypes.find((type) => type.id === this.form.data.type);
    if (leaveType && this.inputLeaveTypes.includes(leaveType.name) && currentDate > new Date()) {
      return true;
    }

    return !(this.userLeavesDatesStrings.includes(val))
      && (this.duties.includes(val) || (currentDate.getDay() !== 0 && currentDate.getDay() !== 6));
  }

  private checkInput(leaveTypeId: number): void {
    const leaveType = this.leaveTypes.find((type) => type.id === leaveTypeId);
    if (!leaveType) {
      return;
    }

    this.documentInput = this.inputLeaveTypes.includes(leaveType.name);
  }

  private validateDates(dates: string[]): void {
    const leaveType = this.leaveTypes.find((type) => type.id === this.form.data.type);
    if (leaveType && leaveType.maxDays !== -1 && this.inputLeaveTypes.includes(leaveType.name) && dates.length === 1) {
      const endDate = new Date(dates[0]);
      endDate.setDate(endDate.getDate() + (leaveType.maxDays - 1));
      const range = this.getDatesArray(dates[0], endDate.toISOString()
        .substr(0, 10));
      this.form.data.dates = range;
    }
    this.validDates = this.form.data.dates.length > 0;
  }

  private disableLeaveType(leaveType: LeaveTypeInterface): boolean {
    let res = false;
    switch (leaveType.name) {
      case 'Additional company paid leave':
        res = this.employeeLeaves.paidLeaves > 0 || this.employeeLeaves.additionalPaidLeaves === 0;
        break;
      case 'Paid leave':
        res = this.employeeLeaves.paidLeaves === 0;
        break;
    }

    return res;
  }

  private clearSelected(): void {
    this.form.data.dates = [];
  }
}
